import {Sentry} from '../_common/my_sentry.js';
import {userBrowserId} from '../../scripts/common.js';

class StudApi {
    user_id;
    first_name;
    last_name;
    prefix;
    token;

    constructor() {
        this.prefix = BACKEND_URL + '/api/exam';
        this.getToken();
    }

    getToken() {
        const params = new URLSearchParams(window.location.search);
        this.token = params.get('token') || '';
        Sentry.setUser({id: this.token});
        if (!this.token) {
            this.redirectToIndex();
        }
    }

    async _template(chatId) {
        return await postJSON(
            `${this.prefix}/getCode`,
            {chatId},
        );
    }

    async save_task_answer(answer, test_instance_task_id) {
        return await postJSON(
            `${this.prefix}/save_task_answer`,
            {token: this.token, answer, test_instance_task_id},
        );
    }

    async save_photo_answer(formData, test_instance_task_id) {
        return await postBlob(
            `${this.prefix}/save_photo_answer?test_instance_task_id=${test_instance_task_id}&token=${this.token}`,
            formData,
        );
    }

    async init_written_upload(payload) {
        return await postJSON(
            `${this.prefix}/init_written_upload`,
            {token: this.token, ...payload},
        );
    }

    async finalize_written_upload(payload) {
        return await postJSON(
            `${this.prefix}/finalize_written_upload`,
            {token: this.token, ...payload},
        );
    }

    async verify_written_upload(written_solution_id) {
        return await postJSON(
            `${this.prefix}/verify_written_upload`,
            {token: this.token, written_solution_id},
        );
    }

    async delete_written_upload(written_solution_id) {
        return await postJSON(
            `${this.prefix}/delete_written_upload`,
            {token: this.token, written_solution_id},
        );
    }

    async test_event(event_type, test_instance_task_id = null, additional_info = null) {
        try {
            return await postJSON(
                `${this.prefix}/test_event`,
                {
                    token: this.token,
                    test_instance_task_id,
                    event_type,
                    additional_info,
                    idd: userBrowserId,
                },
            );
        } catch (e) {
            console.error(e);
        }
    }

    async create_testlinks(method, datatable) {
        return await postJSON(
            `${this.prefix}/create_testlinks`,
            {method, datatable},
        );
    }

    async get_all_testlinks(method, datatable) {
        return await postJSON(
            `${this.prefix}/get_all_testlinks`,
            {method, datatable},
        );
    }

    async get_test_link_status() {
        return await postJSON(
            `${this.prefix}/get_test_link_status`,
            {token: this.token},
        );
    }

    async get_problems_to_solve() {
        return await postJSON(
            `${this.prefix}/get_problems_to_solve`,
            {token: this.token},
        );
    }

    async get_uploaded_written_solutions() {
        return await postJSON(
            `${this.prefix}/get_uploaded_written_solutions`,
            {token: this.token},
        );
    }

    async get_gostreaming_token() {
        return await postJSON(
            `${this.prefix}/get_gostreaming_token`,
            {token: this.token},
        );
    }

    async finish_exam() {
        return await postJSON(
            `${this.prefix}/finish_exam`,
            {token: this.token},
        );
    }

    async finish_written_upload() {
        return await postJSON(
            `${this.prefix}/finish_written_upload`,
            {token: this.token},
        );
    }

    async finish_constructor_session_by_lms(sessionFinishUrl = null) {
        const payload = {token: this.token};
        if (sessionFinishUrl) {
            payload.sessionFinishUrl = sessionFinishUrl;
        }
        return await postJSON(
            `${this.prefix}/constructor/finish_session_by_lms`,
            payload,
        );
    }

    async ask_question(test_instance_task_id, question) {
        return await postJSON(
            `${this.prefix}/ask_question`,
            {token: this.token, test_instance_task_id, question},
        );
    }

    async get_chat_history(test_instance_task_id) {
        return await postJSON(
            `${this.prefix}/get_chat_history`,
            {token: this.token, test_instance_task_id},
        );
    }

    async start_math_test() {
        return await postJSON(
            `${this.prefix}/start_math_test`,
            {token: this.token},
        );
    }

    async start_exam() {
        return await postJSON(
            `${this.prefix}/start`,
            {token: this.token},
        );
    }

    async get_english_state() {
        return await postJSON(
            `${this.prefix}/english/state`,
            {token: this.token},
        );
    }

    async save_english_answer(part_id, answer, revision) {
        return await postJSON(
            `${this.prefix}/english/answer`,
            {token: this.token, part_id, answer, revision},
        );
    }

    async claim_english_audio(media_id) {
        return await postJSON(
            `${this.prefix}/english/audio/claim`,
            {token: this.token, media_id},
        );
    }

    async mark_english_audio_started(media_id, session_token) {
        return await postJSON(
            `${this.prefix}/english/audio/started`,
            {token: this.token, media_id, session_token},
        );
    }

    async update_english_audio_progress(media_id, session_token, position_ms, ended = false) {
        return await postJSON(
            `${this.prefix}/english/audio/progress`,
            {token: this.token, media_id, session_token, position_ms, ended},
        );
    }

    async set_test_link_proctor_started() {
        return await postJSON(
            `${this.prefix}/set_test_link_proctor_started`,
            {token: this.token},
        );
    }

    async get_selected_program() {
        return await postJSON(
            `${this.prefix}/get_selected_program`,
            {token: this.token},
        );
    }

    async save_selected_program(selected) {
        return await postJSON(
            `${this.prefix}/save_selected_program`,
            {token: this.token, selected},
        );
    }


    async get_timeslots_by_program(program) {
        return await postJSON(
            `${this.prefix}/get_timeslots_by_program`,
            {token: this.token, program},
        );
    }

    async delete_selected_slot() {
        return await postJSON(
            `${this.prefix}/delete_selected_slot`,
            {token: this.token},
        );
    }

    async take_selected_slot(slot_id, selected_timezone, selected_timezone_utc_diff) {
        return await postJSON(
            `${this.prefix}/take_selected_slot`,
            {token: this.token, slot_id, selected_timezone, selected_timezone_utc_diff},
        );
    }

    async get_constructor_start_redirect() {
        console.log('[Constructor][studApi] Request /get_constructor_start_redirect', {
            token: maskToken(this.token),
        });
        const response = await postJSON(
            `${this.prefix}/get_constructor_start_redirect`,
            {token: this.token},
        );
        console.log('[Constructor][studApi] Response /get_constructor_start_redirect', {
            status: response?.status,
            msg: response?.msg,
            proctorUrl: response?.proctorUrl,
            startUrl: response?.startUrl,
            integrationName: response?.integrationName,
            method: response?.method,
        });
        return response;
    }

    redirectToIndex() {
        window.location.href = `/index.html`;
    }

    setHeaderName() {
        const forUserName = document.getElementById('adminUserName');
        if (forUserName) {
            forUserName.innerText = `${this.first_name} ${this.last_name}`;
        }
    }
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function maskToken(token, left = 6, right = 4) {
    const raw = token == null ? '' : String(token);
    if (raw.length <= left + right + 3) {
        return raw;
    }
    return `${raw.slice(0, left)}...${raw.slice(-right)}`;
}

async function postJSON(url, data) {
    let lastError = null; // Сохраняем последнюю ошибку

    for (let attempt = 1; attempt <= 2; attempt++) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                let errorMessage = `HTTP error! status: ${response.status} on attempt ${attempt}`;
                try {
                    const payload = await response.json();
                    if (payload && typeof payload.msg === 'string' && payload.msg.trim()) {
                        errorMessage = payload.msg.trim();
                    }
                } catch (e) {
                }
                throw new Error(errorMessage);
            }

            // Если запрос успешен, парсим и возвращаем результат
            return await response.json();

        } catch (error) {
            lastError = error; // Запоминаем ошибку текущей попытки
            console.warn(`Attempt ${attempt} failed for POST ${url}: ${error.message}`);

            if (attempt === 1) {
                // Если это первая неудачная попытка, ждем 1 секунду
                console.log('Retrying in 1 second...');
                await delay(1000);
                // Цикл продолжится на вторую попытку
            } else {
                // Если это вторая неудачная попытка, выводим сообщение и выходим из цикла
                console.error('Second attempt failed. Giving up.');
                break; // Выходим из цикла, чтобы выбросить lastError ниже
            }
        }
    }

    // Если обе попытки не удались (мы вышли из цикла), выбрасываем последнюю ошибку
    console.error(`Failed to POST to ${url} after 2 attempts.`);
    throw lastError; // Выбрасываем ошибку, сохраненную после последней неудачной попытки
}

async function postBlob(url, formData) {
    let lastError = null; // Сохраняем последнюю ошибку

    for (let attempt = 1; attempt <= 2; attempt++) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                // 'Content-Type' для FormData устанавливается браузером автоматически с boundary
                body: formData,
            });

            if (!response.ok) {
                // Генерируем ошибку, чтобы попасть в catch блок для统一обработки
                throw new Error(`HTTP error! status: ${response.status} on attempt ${attempt}`);
            }

            // Если запрос успешен, парсим и возвращаем результат
            return await response.json();

        } catch (error) {
            lastError = error; // Запоминаем ошибку текущей попытки
            console.warn(`Attempt ${attempt} failed for POST (blob) ${url}: ${error.message}`);

            if (attempt === 1) {
                // Если это первая неудачная попытка, ждем 1 секунду
                console.log('Retrying blob upload in 1 second...');
                await delay(1000);
                // Цикл продолжится на вторую попытку
            } else {
                // Если это вторая неудачная попытка, выводим сообщение и выходим из цикла
                console.error('Second blob upload attempt failed. Giving up.');
                break; // Выходим из цикла, чтобы выбросить lastError ниже
            }
        }
    }

    // Если обе попытки не удались (мы вышли из цикла), выбрасываем последнюю ошибку
    console.error(`Failed to POST (blob) to ${url} after 2 attempts.`);
    throw lastError; // Выбрасываем ошибку, сохраненную после последней неудачной попытки
}


function getCookieValue(name) {
    const regex = new RegExp(`(^| )${name}=([^;]+)`);
    const match = document.cookie.match(regex);
    if (match) {
        return match[2];
    }
}

export const studApi = new StudApi();

export const EVENT_TYPE = Object.freeze({
    PAGE_OPEN: "page_open",
    THIS_IS_ME: "me",
    NOT_ME: "not_me",
    UNDERSTAND: "understand",
    READY_TO_START: "ready",
    MIC_OK: "mic_ok",
    MIC_FAIL: "mic_fail",
    SCREEN_OK: "screen_ok",
    SCREEN_FAIL: "screen_fail",
    SCREEN_STOPPED: "screen_stop",
    STARTING_TEST: "starting_test",
    FOCUS_LOST: "focus_lost",
    FOCUS_GAINED: "focus_gained",
    COPY: "copy",
    PASTE: "paste",
    ANSWER_SENT: "answer_sent",
    PROBLEM_SELECTED: "prob_sel",
    UNKOWN_STATE: "UNKOWN_STATE",
    OPEN_AFTER_FINISH: "after_finish",
    OPEN_WRITTEN_UPLOAD: "open_written_upload",
    OPEN_AFTER_START: "open_after_start",
    FINISH_PRESSED: "finish?",
    FINISH_CANCEL: "finish_cancel",
    FINISH_APPROVE: "really_finish",
    RESIZE: "resize",
    CONTEXTMENU: "contextmenu",
    UNLOAD: "unload",
    KEYDOWN: "keydown",
    DEVTOOLS: "devtools",
    SUPPORT_QUESTION: "support_question",
    SUPPORT_ANSWER: "support_answer",
    PROCTORED_EXAM_STARTED: "proctored_exam_started",
    PROCTORING_START: "proctoring_start",
    PROCTORING_STOP: "proctoring_stop",
    PROCTORING_SCREENSHOT_OK: "proctoring_screenshot_ok",
    PROCTORING_SCREENSHOT_FAIL: "proctoring_screenshot_fail",
    PROCTORING_CAMERA_OK: "proctoring_camera_ok",
    PROCTORING_CAMERA_FAIL: "proctoring_camera_fail",
    PROCTORING_AUDIO_OK: "proctoring_audio_ok",
    PROCTORING_AUDIO_FAIL: "proctoring_audio_fail",
    PROCTORING_UNSUPPORTED_BROWSER: "proctoring_unsupported_browser",
    PROCTORING_SECOND_MONITOR: "proctoring_second_monitor",
    PROCTORING_SCREEN_NOT_MONITOR: "proctoring_screen_not_monitor",
    IMAGE_UPLOAD_STARTED: "image_upload_started",
    IMAGE_SENT: "image_sent",
    IMAGE_SEND_ERROR: "image_sent_error",
    PROGRAM_SELECT: "program_select",
    TIMESLOT_SELECT: "timeslot_select",
    IMAGE_SELECTED: "image_selected",
});

export const TEST_LINK_STATE = Object.freeze({
    NOT_OPENED: "not_op",             // Тестовая ссылка ещё не открывалась
    OPENED_NOT_STARTED: "op_not_start", // Ссылка открыта, но тест не начат
    STARTED_NOT_FINISHED: "start_not_fin", // Тест начат, но не окончен
    FIN_WRITTEN_UPLOAD: "fin_written_upload", // Тест окончен, происходит загрузка скриншотов
    FINISHED: "fin"                   // Тест завершён
});

async function deriveKey(token) {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        encoder.encode(token),
        {name: "PBKDF2"},
        false,
        ["deriveKey"]
    );

    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: encoder.encode("test-salt"), // Фиксированная соль (можно усложнить)
            iterations: 100000,
            hash: "SHA-256"
        },
        keyMaterial,
        {name: "AES-GCM", length: 256},
        false,
        ["encrypt", "decrypt"]
    );
}
